<template>
    <div class="image-download-status">
        <div class="d-flex align-center">
            <!-- Progress Bar -->
            <div class="progress-container flex-grow-1">
                <v-progress-linear
                    :value="progressPercentage"
                    height="24"
                    striped
                    stream
                    buffer-value="0"
                    color="primary"
                    class="rounded-lg"
                >
                    <template v-slot:default>
                        <div class="d-flex align-center justify-space-between white--text">
                            <v-chip small class="progress-chip" dark>
                                <span class="font-weight-medium mr-4">
                                    {{ downloadStatus.counts.downloaded }} / {{ downloadStatus.counts.total }} images downloaded
                                </span>
                                <span class="font-weight-bold">
                                    {{ progressPercentage }}%
                                </span>
                            </v-chip>
                        </div>
                    </template>
                </v-progress-linear>
            </div>

            <!-- Info Button with Tooltip -->
            <v-tooltip 
                bottom 
                color="grey darken-2 rounded-lg"
                :value="tooltipOpen"
                :max-width="tooltipMaxWidth"
                content-class="scrollable-tooltip"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        small
                        class="ml-3"
                        :class="{ 'info-btn-active': isPinned }"
                        v-bind="attrs"
                        @mouseenter="handleMouseEnter"
                        @mouseleave="handleMouseLeave"
                        @click="toggleTooltip"
                    >
                        <v-icon 
                            color="primary"
                            :class="{ 'icon-active': isPinned }"
                        >
                            mdi-information-outline
                        </v-icon>
                    </v-btn>
                </template>
                <div 
                    class="status-tooltip"
                    @mouseenter="handleTooltipEnter"
                    @mouseleave="handleTooltipLeave"
                >
                    <div class="tooltip-header mb-2">
                        <h3>Image Download Status</h3>
                    </div>
                    <v-divider dark class="mb-2"></v-divider>
                    <div 
                        class="tooltip-dates-grid"
                        :class="{ 'multi-column': sortedDatesArray.length > 10 }"
                    >
                        <div 
                            v-for="([date, status], index) in sortedDatesArray" 
                            :key="date"
                            class="tooltip-item d-flex align-center justify-space-between mb-1"
                        >
                            <span class="date-text">{{ formatDate(date) }}</span>
                            <v-chip
                                x-small
                                :color="getStatusColor(status)"
                                dark
                                class="ml-3"
                            >
                                <v-icon x-small left>
                                    {{ getStatusIcon(status) }}
                                </v-icon>
                                {{ status }}
                            </v-chip>
                        </div>
                    </div>
                </div>
            </v-tooltip>
        </div>
    </div>
</template>

<script>
export default {
    name: "ImageDownloadStatusProgressBar",
    
    props: {
        downloadStatus: {
            type: [Object, null],
            required: true,
            default: () => ({
                dates: {},
                counts: {
                    downloaded: 0,
                    pending: 0,
                    total: 0
                }
            })
        },
        isRefreshing: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            tooltipOpen: false,
            isPinned: false,
            closeTimer: null,
            isMouseOverTooltip: false,
            isMouseOverButton: false
        };
    },

    computed: {
        progressPercentage() {
            if (!this.downloadStatus || this.downloadStatus.counts.total === 0) return 0;
            return Math.round(
                (this.downloadStatus.counts.downloaded / this.downloadStatus.counts.total) * 100
            );
        },

        sortedDatesArray() {
            if (!this.downloadStatus || !this.downloadStatus.dates) return [];
            
            return Object.entries(this.downloadStatus.dates)
                .sort((a, b) => new Date(a[0]) - new Date(b[0]));
        },

        tooltipMaxWidth() {
            return this.sortedDatesArray.length > 10 ? '800px' : '400px';
        }
    },

    methods: {
        formatDate(dateString) {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
            });
        },

        getStatusColor(status) {
            return status === 'downloaded' ? 'success' : 'warning';
        },

        getStatusIcon(status) {
            return status === 'downloaded' ? 'mdi-check-circle' : 'mdi-clock-outline';
        },

        toggleTooltip() {
            this.isPinned = !this.isPinned;
            this.tooltipOpen = this.isPinned;
            
            if (this.closeTimer) {
                clearTimeout(this.closeTimer);
                this.closeTimer = null;
            }
        },

        handleMouseEnter() {
            this.isMouseOverButton = true;
            this.tooltipOpen = true;
            
            if (this.closeTimer) {
                clearTimeout(this.closeTimer);
                this.closeTimer = null;
            }
        },

        handleMouseLeave() {
            this.isMouseOverButton = false;
            
            if (!this.isPinned && !this.isMouseOverTooltip) {
                this.scheduleClose();
            }
        },

        handleTooltipEnter() {
            this.isMouseOverTooltip = true;
            
            if (this.closeTimer) {
                clearTimeout(this.closeTimer);
                this.closeTimer = null;
            }
        },

        handleTooltipLeave() {
            this.isMouseOverTooltip = false;
            
            if (!this.isPinned && !this.isMouseOverButton) {
                this.scheduleClose();
            }
        },

        scheduleClose() {
            if (this.closeTimer) {
                clearTimeout(this.closeTimer);
            }
            
            this.closeTimer = setTimeout(() => {
                if (!this.isPinned && !this.isMouseOverButton && !this.isMouseOverTooltip) {
                    this.tooltipOpen = false;
                }
            }, 300);
        }
    },

    beforeDestroy() {
        if (this.closeTimer) {
            clearTimeout(this.closeTimer);
        }
    }
}
</script>

<style>
/* Global style (not scoped) to allow tooltip interaction */
.scrollable-tooltip {
    pointer-events: auto !important;
}
</style>

<style scoped>
.image-download-status {
    margin-bottom: 20px;
}

.progress-container {
    min-width: 0;
}

.status-tooltip {
    padding: 12px;
    cursor: default;
}

.tooltip-header {
    font-size: 14px;
}

.tooltip-header h3 {
    font-size: 16px;
    margin: 0;
}

.tooltip-dates-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 4px;
    max-height: 400px;
    overflow-y: auto;
}

.tooltip-dates-grid.multi-column {
    grid-template-columns: repeat(2, 1fr);
    gap: 4px 2rem;
}

@media (min-width: 900px) {
    .tooltip-dates-grid.multi-column {
        grid-template-columns: repeat(3, 1fr);
    }
}

.tooltip-item {
    font-size: 13px;
    padding: 0px 8px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
    min-width: 200px;
}

.tooltip-item:hover {
    background-color: rgba(255, 255, 255, 0.1);
}

.date-text {
    white-space: nowrap;
    flex-shrink: 0;
}

.progress-chip {
    min-width: 180px;
    background-color: rgba(31, 75, 100, 0.699) !important;
    backdrop-filter: blur(10px);
    justify-content: space-between;
}

/* Active state for info button when tooltip is pinned */
.info-btn-active {
    background-color: rgba(17, 94, 170, 0.305) !important;
    transition: background-color 0.2s ease;
}

.info-btn-active:hover {
    background-color: rgba(25, 118, 210, 0.2) !important;
}

/* Active state for icon - adds a subtle animation */
.icon-active {
    animation: pulse-icon 2s ease-in-out infinite;
}

@keyframes pulse-icon {
    0%, 100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.8;
        transform: scale(1.1);
    }
}

.tooltip-dates-grid::-webkit-scrollbar {
    width: 8px;
}

.tooltip-dates-grid::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
}

.tooltip-dates-grid::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
}

.tooltip-dates-grid::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
}
</style>